import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User as UserIcon } from "lucide-react";
import { FaDiscord, FaGoogle } from "react-icons/fa";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success("Berhasil masuk!");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const oauth = (p) => toast.info(`Login ${p} akan aktif setelah admin menyiapkan OAuth credentials.`);

  return (
    <div className="mc-bg pixel-grid min-h-screen flex items-center justify-center p-4 relative">
      <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(2,132,199,0.35), transparent 70%)" }} />
      <div className="glass-strong rounded-3xl p-8 w-full max-w-md relative z-10" data-testid="auth-card">
        <div className="flex flex-col items-center mb-6">
          <img src="/logo.png" alt="logo" className="w-16 h-16 rounded-2xl mb-3 animate-float" />
          <h1 className="font-heading text-3xl font-extrabold text-white text-glow">Dreamer Hosting</h1>
          <p className="text-sm text-slate-400 mt-1">Fast and Reliable</p>
        </div>

        <div className="flex gap-2 p-1 rounded-xl bg-slate-900/60 mb-6">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => { setMode(m); setError(""); }}
              data-testid={`auth-tab-${m}`}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all
                ${mode === m ? "btn-cyan text-white" : "text-slate-400 hover:text-white"}`}>
              {m === "login" ? "Masuk" : "Daftar"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <Label className="text-slate-300 text-sm">Nama</Label>
              <div className="relative mt-1">
                <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required placeholder="Nama kamu" data-testid="register-name-input"
                  className="pl-9 bg-slate-900/60 border-cyan-500/20 text-white" />
              </div>
            </div>
          )}
          <div>
            <Label className="text-slate-300 text-sm">Email</Label>
            <div className="relative mt-1">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                required placeholder="email@contoh.com" data-testid="email-input"
                className="pl-9 bg-slate-900/60 border-cyan-500/20 text-white" />
            </div>
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Password</Label>
            <div className="relative mt-1">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                required placeholder="••••••••" data-testid="password-input"
                className="pl-9 bg-slate-900/60 border-cyan-500/20 text-white" />
            </div>
          </div>

          {error && <p className="text-sm text-rose-400" data-testid="auth-error">{error}</p>}

          <Button type="submit" disabled={loading} className="btn-cyan w-full text-white border-0 h-11" data-testid="auth-submit-btn">
            {loading && <Loader2 size={16} className="mr-2 animate-spin" />}
            {mode === "login" ? "Masuk" : "Buat Akun"}
          </Button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-slate-700/50" />
          <span className="text-xs text-slate-500">atau</span>
          <div className="h-px flex-1 bg-slate-700/50" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => oauth("Discord")} data-testid="login-discord-btn"
            className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#5865F2]/20 border border-[#5865F2]/40 text-indigo-300 hover:bg-[#5865F2] hover:text-white transition-all text-sm font-medium">
            <FaDiscord size={18} /> Discord
          </button>
          <button onClick={() => oauth("Google")} data-testid="login-google-btn"
            className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-slate-800/60 border border-slate-600/40 text-slate-300 hover:bg-white hover:text-slate-900 transition-all text-sm font-medium">
            <FaGoogle size={16} /> Google
          </button>
        </div>
      </div>
    </div>
  );
}
